
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, CheckCircle, Percent, Building, Zap, ArrowRight, Trophy } from "lucide-react";

interface NewWelcomeStepProps {
  onComplete: () => void;
  onPrevious: () => void;
}

export const NewWelcomeStep = ({ onComplete, onPrevious }: NewWelcomeStepProps) => {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <Trophy className="w-16 h-16 mx-auto mb-4 text-primary" />
        <h2 className="text-3xl font-semibold text-card-foreground mb-3">Parabéns! 🎉</h2>
        <p className="text-muted-foreground text-lg">
          Sua configuração foi concluída com sucesso. Agora você está pronto para começar!
        </p>
      </div>

      {/* Resumo das Configurações */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold text-card-foreground mb-4">Configurações Realizadas</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="font-medium text-card-foreground">Termos Aceitos</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Termos de uso da plataforma aceitos
              </p>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Percent className="w-5 h-5 text-primary" />
                <span className="font-medium text-card-foreground">Cashback Ativo</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Programa de cashback configurado
              </p>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Building className="w-5 h-5 text-primary" />
                <span className="font-medium text-card-foreground">Perfil Completo</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Dados da empresa cadastrados
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Próximos Passos */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold text-card-foreground mb-4">Próximos Passos</h3>
          
          <div className="space-y-3">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                <span className="text-primary font-semibold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-medium text-card-foreground">Conectar sua Loja</h4>
                <p className="text-sm text-muted-foreground">
                  Configure a integração com Nuvemshop ou outras plataformas
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                <span className="text-primary font-semibold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-medium text-card-foreground">Acompanhar Resultados</h4>
                <p className="text-sm text-muted-foreground">
                  Monitore métricas e otimize sua estratégia de cashback
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recursos Disponíveis */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold text-card-foreground mb-4 flex items-center gap-2">
            <Zap className="w-6 h-6 text-primary" />
            Recursos Disponíveis
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-card-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Cashback automático para clientes
                </li>
                <li className="flex items-center gap-2 text-card-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Sistema de indicações/referral
                </li>
                <li className="flex items-center gap-2 text-card-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Campanhas personalizadas
                </li>
              </ul>
            </div>
            <div className="space-y-2">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-card-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Relatórios e analytics
                </li>
                <li className="flex items-center gap-2 text-card-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Notificações automáticas
                </li>
                <li className="flex items-center gap-2 text-card-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Suporte especializado
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navegação */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrevious} className="flex-1">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Button onClick={onComplete} className="flex-1 text-lg py-6">
          Iniciar Plataforma
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
};
